import { useEffect, useState } from "react";
import { Sun, Moon, Cloud, CloudSun, CloudMoon, CloudRain, CloudSnow, CloudDrizzle, CloudLightning, CloudFog, MapPin } from "lucide-react";

const WMO = [
  { codes: [0], label: "Grand soleil", nightLabel: "Nuit claire", kind: "sun" },
  { codes: [1, 2], label: "Éclaircies", nightLabel: "Nuit étoilée", kind: "suncloud" },
  { codes: [3], label: "Couvert", kind: "cloud" },
  { codes: [45, 48], label: "Brouillard", kind: "fog" },
  { codes: [51, 53, 55, 56, 57, 80], label: "Bruine", kind: "drizzle" },
  { codes: [61, 63, 65, 66, 67, 81, 82], label: "Pluie", kind: "rain" },
  { codes: [71, 73, 75, 77, 85, 86], label: "Neige", kind: "snow" },
  { codes: [95, 96, 99], label: "Orage", kind: "storm" },
];

const ICONS_DAY = { sun: Sun, suncloud: CloudSun, cloud: Cloud, fog: CloudFog, drizzle: CloudDrizzle, rain: CloudRain, snow: CloudSnow, storm: CloudLightning };
const ICONS_NIGHT = { ...ICONS_DAY, sun: Moon, suncloud: CloudMoon };

const DAY_BG = {
  sun: "from-sky-400 via-sky-300 to-sky-200 border-sky-300/70",
  suncloud: "from-sky-300 via-sky-200 to-emerald-100 border-sky-200/70",
  cloud: "from-slate-300 via-slate-200 to-sky-100 border-slate-300/70",
  fog: "from-slate-200 via-slate-100 to-slate-200 border-slate-300/60",
  drizzle: "from-slate-300 via-sky-200 to-slate-200 border-slate-300/70",
  rain: "from-slate-400 via-sky-300 to-slate-300 border-slate-400/70",
  snow: "from-slate-100 via-sky-50 to-slate-100 border-slate-200/80",
  storm: "from-slate-500 via-slate-400 to-slate-500 border-slate-500/70",
};
const NIGHT_BG = "from-[#081631] via-[#10254d] to-[#081631] border-[#1e3a66]";

const STARS = [
  { left: "5%", top: "26%", d: "0s" }, { left: "12%", top: "62%", d: "0.5s" }, { left: "21%", top: "18%", d: "1.1s" },
  { left: "30%", top: "55%", d: "1.7s" }, { left: "38%", top: "22%", d: "0.3s" }, { left: "47%", top: "66%", d: "1.4s" },
  { left: "55%", top: "20%", d: "0.9s" }, { left: "64%", top: "58%", d: "2s" }, { left: "72%", top: "24%", d: "0.2s" },
  { left: "81%", top: "60%", d: "1.2s" }, { left: "89%", top: "30%", d: "1.8s" }, { left: "96%", top: "52%", d: "0.7s" },
];

function AnimatedIcon({ kind, night }) {
  const Icon = (night ? ICONS_NIGHT : ICONS_DAY)[kind] || CloudSun;
  const color =
    kind === "sun" ? (night ? "text-amber-200" : "text-amber-500 wx-sun")
    : kind === "suncloud" ? (night ? "text-sky-200 wx-cloud" : "text-amber-500 wx-cloud")
    : night ? "text-sky-200 wx-cloud" : "text-sky-700 wx-cloud";
  return (
    <span className="relative inline-flex items-center justify-center w-8 h-8 shrink-0" aria-hidden="true">
      <Icon className={`h-6 w-6 ${color}`} />
      {kind === "rain" && (
        <>
          <span className="wx-drop" style={{ left: "8px" }} />
          <span className="wx-drop" style={{ left: "14px", animationDelay: "0.35s" }} />
          <span className="wx-drop" style={{ left: "20px", animationDelay: "0.7s" }} />
        </>
      )}
      {kind === "snow" && (
        <>
          <span className="wx-flake" style={{ left: "8px" }} />
          <span className="wx-flake" style={{ left: "14px", animationDelay: "0.4s" }} />
          <span className="wx-flake" style={{ left: "20px", animationDelay: "0.8s" }} />
        </>
      )}
    </span>
  );
}

export default function WeatherBanner() {
  const [weather, setWeather] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = () =>
      fetch("https://api.open-meteo.com/v1/forecast?latitude=48.194&longitude=1.936&current=temperature_2m,weather_code,is_day&hourly=temperature_2m,weather_code&forecast_days=2&timezone=Europe%2FParis")
        .then((r) => r.json())
        .then((d) => {
          const code = d?.current?.weather_code ?? 2;
          const found = WMO.find((w) => w.codes.includes(code)) || WMO[1];
          const isDay = d?.current?.is_day !== 0;
          const todayStr = (d?.current?.time || "").slice(0, 10);
          let tomorrow = null;
          const times = d?.hourly?.time || [];
          const temps = d?.hourly?.temperature_2m || [];
          const codes = d?.hourly?.weather_code || [];
          const pick = (hh) => {
            const i = times.findIndex((t) => t.slice(0, 10) > todayStr && t.endsWith(`T${hh}:00`));
            if (i < 0) return null;
            const f = WMO.find((w) => w.codes.includes(codes[i])) || WMO[1];
            return { temp: Math.round(temps[i]), kind: f.kind };
          };
          const matin = pick("09");
          const aprem = pick("15");
          if (matin || aprem) tomorrow = { matin, aprem };
          setWeather({ temp: Math.round(d?.current?.temperature_2m ?? 0), isDay, tomorrow, ...found });
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const night = weather ? !weather.isDay : false;
  const bg = night ? NIGHT_BG : (weather ? DAY_BG[weather.kind] : null) || DAY_BG.suncloud;
  const label = weather ? (night && weather.nightLabel ? weather.nightLabel : weather.label) : "";

  const dateStr = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" }).format(now);
  const timeStr = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(now);

  return (
    <div className={`theme-keep relative overflow-hidden bg-gradient-to-r border-b transition-colors duration-700 ${bg} ${night ? "text-sky-100" : "text-slate-800"}`} data-testid="weather-banner">
      {night && STARS.map((s, i) => (
        <span key={i} className="wx-star" style={{ left: s.left, top: s.top, animationDelay: s.d }} aria-hidden="true" />
      ))}
      {!night && weather?.kind === "sun" && <span className="wx-sun-orb" aria-hidden="true" />}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-1.5 flex flex-wrap items-center justify-center sm:justify-between gap-x-5 gap-y-1">
          <div className={`flex flex-col justify-center leading-tight ${night ? "text-sky-100" : "text-sky-900"}`} data-testid="weather-time">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] opacity-75 capitalize">{dateStr}</span>
            <span className="font-heading text-xl font-extrabold tabular-nums tracking-wide">{timeStr}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-widest ${night ? "text-emerald-300" : "text-emerald-700"}`}>
              <MapPin className="h-3.5 w-3.5" />Toury
            </span>
            {weather ? (
              <span className="flex items-center gap-2" data-testid="weather-info">
                <AnimatedIcon kind={weather.kind} night={night} />
                <span className={`text-sm font-semibold ${night ? "text-sky-50" : "text-slate-700"}`}>{weather.temp}°C</span>
                <span className={`text-xs ${night ? "text-sky-300" : "text-slate-500"}`}>{label}</span>
              </span>
            ) : (
              <span className={`text-xs ${night ? "text-sky-300" : "text-slate-400"}`} data-testid="weather-loading">Météo en cours…</span>
            )}
          </div>
        </div>
        {weather?.tomorrow && (
          <div className={`border-t ${night ? "border-white/15" : "border-sky-900/10"} py-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs`} data-testid="weather-tomorrow">
            <span className={`font-semibold uppercase tracking-widest ${night ? "text-sky-300" : "text-slate-500"}`}>Prévisions pour demain</span>
            {weather.tomorrow.matin && (
              <span className="flex items-center gap-1.5" data-testid="weather-tomorrow-matin">
                <span className={night ? "text-sky-200" : "text-slate-600"}>Matin</span>
                <AnimatedIcon kind={weather.tomorrow.matin.kind} night={false} />
                <span className="font-semibold">{weather.tomorrow.matin.temp}°C</span>
              </span>
            )}
            {weather.tomorrow.aprem && (
              <span className="flex items-center gap-1.5" data-testid="weather-tomorrow-aprem">
                <span className={night ? "text-sky-200" : "text-slate-600"}>Après-midi</span>
                <AnimatedIcon kind={weather.tomorrow.aprem.kind} night={false} />
                <span className="font-semibold">{weather.tomorrow.aprem.temp}°C</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
